/** Quixo OS staffed handoff queue. No chat transcript is shown or forwarded automatically. */
import { useEffect, useState } from 'react';
import { bff } from '../../../lib/bff';
import { useSurface } from '../kit/context';
import { Screen } from '../kit/ui';
import './support-queue.css';

type CaseRow = {
  id: string;
  tenantId: string;
  requesterId: string;
  subject: string;
  description: string;
  priority: 'normal' | 'high';
  status: 'open' | 'in_progress' | 'resolved';
  assignedTo: string | null;
  createdAt: string;
};

export function SupportQueuePage() {
  const { t } = useSurface();
  const [rows, setRows] = useState<CaseRow[]>([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [message, setMessage] = useState(false);

  async function refresh() {
    setError(false);
    try {
      const data = await bff<CaseRow[]>('/v1/ai/admin/support/cases');
      setRows(data);
      setLoaded(true);
    } catch {
      setError(true);
    }
  }
  useEffect(() => { void refresh(); }, []);

  async function setStatus(id: string, status: 'open' | 'in_progress' | 'resolved') {
    if (busy || reason.trim().length < 10) return;
    setBusy(id);
    setMessage(false);
    setError(false);
    try {
      await bff<{id:string;status:string}>(('/v1/ai/admin/support/cases/' + encodeURIComponent(id)) as `/v1/${string}`, {
        method: 'PATCH',
        body: JSON.stringify({ status, reason: reason.trim() }),
      });
      setReason('');
      setMessage(true);
      await refresh();
    } catch {
      setError(true);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Screen title={t('portal.admin.supportQueue.title')} subtitle={t('portal.admin.supportQueue.intro')} testId="support-queue">
      <div className="qm-staff-support">
        <div className="qm-staff-support__controls">
          <label htmlFor="qm-staff-support-reason">{t('portal.admin.supportQueue.reason')}</label>
          <input id="qm-staff-support-reason" maxLength={500} minLength={10} value={reason}
            placeholder={t('portal.admin.supportQueue.reasonHint')}
            onChange={(e) => setReason(e.target.value)} />
          <button type="button" onClick={() => void refresh()} disabled={busy !== null}>{t('portal.admin.supportQueue.refresh')}</button>
        </div>
        {error && <p role="alert">{t('portal.admin.supportQueue.failure')}</p>}
        {message && <p role="status">{t('portal.admin.supportQueue.updated')}</p>}
        {loaded && rows.length === 0 && <p>{t('portal.admin.supportQueue.empty')}</p>}
        <div className="qm-staff-support__grid">
          {rows.map((caseRow) => (
            <article key={caseRow.id} className="qm-staff-support__case">
              <header>
                <strong>{caseRow.subject}</strong>
                <span>{t('portal.admin.supportQueue.priority.' + caseRow.priority)}</span>
              </header>
              <p>{caseRow.description}</p>
              <dl>
                <div><dt>{t('portal.admin.supportQueue.caseId')}</dt><dd>{caseRow.id}</dd></div>
                <div><dt>{t('portal.admin.supportQueue.tenant')}</dt><dd>{caseRow.tenantId}</dd></div>
                <div><dt>{t('portal.admin.supportQueue.status')}</dt><dd>{t('portal.admin.supportQueue.status.' + caseRow.status)}</dd></div>
              </dl>
              <div className="qm-staff-support__actions">
                {caseRow.status !== 'in_progress' && <button type="button" disabled={busy !== null || reason.trim().length < 10}
                  onClick={() => void setStatus(caseRow.id, 'in_progress')}>{t('portal.admin.supportQueue.take')}</button>}
                {caseRow.status !== 'resolved' && <button type="button" disabled={busy !== null || reason.trim().length < 10}
                  onClick={() => void setStatus(caseRow.id, 'resolved')}>{t('portal.admin.supportQueue.resolve')}</button>}
              </div>
            </article>
          ))}
        </div>
      </div>
    </Screen>
  );
}
